<script setup>
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useViewStore} from '@/stores/view'
import Button from '@/common/Button.vue'
import {getTypeOptions} from "@/utils/filter"

const props = defineProps({
    viewId: String,
    filterKey: Number,
    columns: Array
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Pinia Stores
const componentsStore = useComponentsStore()
const fieldsetsStore = useFieldsetsStore()

const tableName = computed(() => viewStore.tableName)
const filter = computed(() => viewStore.filters?.[props.filterKey])
const columnName = computed(() => filter.value?.columnName)

const columnTitle = computed(() => {
    return props.columns?.find(o => o.columnName === columnName.value)?.title
})

const componentId = computed(() => {
    return componentsStore.componentIds?.[tableName.value]
})

const fieldsets = computed(() => {
    const items = fieldsetsStore.items || []
    const filteredFieldsets = items.filter(o => o.componentId === componentId.value)

    // Return a purely immutable array combination
    return [...filteredFieldsets, {id: 0, legend: '&nbsp;'}]
})

const getColumns = (fieldsetId) => {
    return props.columns?.filter(o => o.fieldsetId === fieldsetId) || []
}

const setFilter = (name, value) => {
    viewStore.setFilterProp({
        key: props.filterKey,
        name: name,
        value: value
    })
}

const setColumn = (columnName) => {
    setFilter('columnName', columnName)

    const column = props.columns?.find(o => o.columnName === columnName)
    if (!column) return

    setFilter('type', column.type)

    const defaultOption = getTypeOptions(column.type)?.[0]
    if (defaultOption) {
        setFilter('condition', defaultOption.condition)
        setFilter('predicate', defaultOption.predicate)
        setFilter('conditionText', defaultOption.text)

        // Unset the previously set value, otherwise it might conflict with the new field's props.
        setFilter('value', defaultOption.defaultValue)
    }
}
</script>

<template>
    <div class="dropdown">
        <!-- Added @click to guarantee Bootstrap's dropdown hook fires on your custom wrapper -->
        <Button data-bs-toggle="dropdown" class="btn-sm border text-nowrap focus-ring">
            {{ columnTitle }}
        </Button>
        <ul class="dropdown-menu pt-0" role="menu">
            <template v-for="(fieldset, index) in fieldsets" :key="index">
                <li class="dropdown-item disabled" :class="{'pt-3': fieldset.legend !== '&nbsp;'}"
                    v-html="fieldset.legend"/>
                <li v-for="column in getColumns(fieldset.id)" :key="column.id || column.columnName"
                    @click="setColumn(column.columnName)">
                    <a href @click.prevent :class="{'active': column.columnName === columnName}" class="dropdown-item">
                        {{ column.title }}
                    </a>
                </li>
            </template>
        </ul>
    </div>
</template>